import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { UPCOMING, VENUES } from '@/data/events';
import { fmtDate } from '@/lib/format';
import { useI18n } from '@/providers/i18n';
import { useCart } from '@/store/cart';

import OrderSummary from './components/OrderSummary';
import TierRow from './components/TierRow';
import styles from './TicketsPage.module.css';

export default function TicketsPage() {
  const { id } = useParams<{ id: string }>();
  const { t, lang } = useI18n();
  const cart = useCart();
  const nav = useNavigate();
  const event = UPCOMING.find((e) => e.id === id) ?? null;

  const [qtys, setQtys] = useState<Record<string, number>>(() =>
    event ? Object.fromEntries(event.tiers.map((tier) => [tier.id, 0])) : {},
  );

  const totals = useMemo(() => {
    if (!event) return { subtotal: 0, fee: 0, total: 0, totalQty: 0 };
    const subtotal = event.tiers.reduce(
      (sum, tier) => sum + (qtys[tier.id] ?? 0) * tier.price,
      0,
    );
    const totalQty = Object.values(qtys).reduce((a, b) => a + b, 0);
    const fee = subtotal ? Math.round(subtotal * 0.07) : 0;
    return { subtotal, fee, total: subtotal + fee, totalQty };
  }, [event, qtys]);

  if (!event) {
    return (
      <section className={styles.notFound}>
        <h1 className={`upper tight ${styles.notFoundTitle}`}>404 / NO TICKET</h1>
        <Link to="/events" className={`btn btn-arrow ${styles.notFoundBack}`}>
          {t('cta.back')}
        </Link>
      </section>
    );
  }

  const dd = fmtDate(event.date, lang);
  const venue = VENUES[event.venue];
  const setQty = (tid: string, n: number) =>
    setQtys((q) => ({ ...q, [tid]: Math.max(0, n) }));

  const addToCart = () => {
    event.tiers.forEach((tier) => {
      const q = qtys[tier.id] ?? 0;
      if (q > 0) {
        cart.add({
          key: `tix:${event.id}:${tier.id}`,
          kind: 'ticket',
          name: `${event.title} — ${tier.name}`,
          variant: `${dd.day} ${dd.month} ${dd.year} · ${venue.name}`,
          price: tier.price,
          qty: q,
        });
      }
    });
    nav('/checkout');
  };

  return (
    <section>
      <header className="page-h">
        <div>
          <span className="nano">/ TICKETS / {event.code}</span>
          <h1>{event.title}</h1>
        </div>
        <span className="meta">
          {dd.day} {dd.month} {dd.year}
        </span>
      </header>

      <div className={styles.layout}>
        <div>
          <div className={`line-row ${styles.stepHead}`}>
            <span>STEP 01</span>
            <span className="line" />
            <span>SELECT TIER & QTY</span>
          </div>

          <div className={styles.tierList}>
            {event.tiers.map((tier) => (
              <TierRow
                key={tier.id}
                tier={tier}
                qty={qtys[tier.id] ?? 0}
                onQtyChange={(n) => setQty(tier.id, n)}
              />
            ))}
          </div>

          <p className={`micro fg-dim ${styles.legal}`}>
            STRICTLY 18+ · FACE CONTROL ENFORCED · NO REFUNDS AFTER WAVE OPENS · ID REQUIRED AT
            DOOR · ORGANIZER MAY REVOKE ENTRY
          </p>
        </div>

        <OrderSummary
          orderRef={event.code}
          totalQty={totals.totalQty}
          subtotal={totals.subtotal}
          fee={totals.fee}
          total={totals.total}
        >
          <button
            type="button"
            className="btn btn-fill btn-arrow btn-block"
            disabled={totals.totalQty === 0}
            onClick={addToCart}
          >
            {t('cta.checkout')}
          </button>
          <Link to={`/events/${event.id}`} className="btn btn-ghost btn-block">
            {t('cta.back')}
          </Link>
        </OrderSummary>
      </div>
    </section>
  );
}
